import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import ShareIcon from '@mui/icons-material/Share';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import BackupTableOutlinedIcon from '@mui/icons-material/BackupTableOutlined';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import {
  FolderManagerActionTypes,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerItemTypes,
} from '../../../interfaces';

import { getActionsForItemType } from '../../../helpers';
import { usePresentationBuildPolling, usePresentationDownload } from '../../../hooks';
import { useGetPresentationQuery } from '../../../services';
import CircularLoader from '../../../shared/components/CircularLoader';
import { useDeepCompareMemo } from '@clatter/platform';

interface DropdownMenuProps {
  anchorEl: null | HTMLElement;
  open: boolean;
  slug: string | undefined;
  isLoading: boolean | undefined;
  item: FolderManagerItem;
  actions?: IFolderManagerActionsItem[];
  selectedItems: FolderManagerItem[] | undefined;
  onClose: (event: React.MouseEvent<HTMLElement>) => void;
  onAction: (action: any) => void;
  itemActions: any;
  isPresentationBuilding?: boolean;
  isDownloadDisabled?: boolean;
  isDownloading?: boolean;
  onPresentationDownload?: () => void;
}

interface IFolderManagerItemActions {
  item: FolderManagerItem;
  selectedItems?: FolderManagerItem[];
  isLoading?: boolean;
  isTableView?: boolean;
  actions?: IFolderManagerActionsItem[];
  onAction: any;
  isMulti?: boolean;
}

interface IGetActionByName {
  slug: string;
  isLoading?: boolean;
  onAction: (data: any) => void;
  onClose: (event: any) => void;
  fontSize?: 'inherit' | 'large' | 'medium' | 'small';
  item: FolderManagerItem;
  selectedItems?: FolderManagerItem[];
  actionName: FolderManagerItemActionNames;
  isPresentationBuilding?: boolean;
  isDownloadDisabled?: boolean;
  isDownloading?: boolean;
  onPresentationDownload?: () => void;
}

const StyledActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
`;

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  anchorEl,
  open,
  onClose,
  itemActions,
  item,
  slug,
  actions,
  onAction,
  isLoading,
  selectedItems,
  isPresentationBuilding,
  isDownloadDisabled,
  isDownloading,
  onPresentationDownload,
}) => {
  return (
    <Menu anchorEl={anchorEl} open={open} onClose={(event) => onClose(event as React.MouseEvent<HTMLElement>)}>
      {(actions || itemActions).map((action: any) =>
        getActionByName({
          item,
          isLoading,
          selectedItems,
          slug: slug || '',
          actionName: action.name,
          onClose: onClose,
          onAction: onAction,
          isPresentationBuilding,
          isDownloadDisabled,
          isDownloading,
          onPresentationDownload,
        }),
      )}
    </Menu>
  );
};

const themeActions = [
  { name: FolderManagerItemActionNames.RENAME, type: FolderManagerItemActionTypes.SINGLE },
  // { name: FolderManagerItemActionNames.MOVE, type: FolderManagerItemActionTypes.MULTI },
  // { name: FolderManagerItemActionNames.COPY, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.DOWNLOAD, type: FolderManagerItemActionTypes.MULTI },
  // { name: FolderManagerItemActionNames.MANAGE_THEME, type: FolderManagerItemActionTypes.SINGLE }, // @todo: enable when themes will be ready
  { name: FolderManagerItemActionNames.MANAGE_VERSIONS, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.RESTORE, type: FolderManagerItemActionTypes.MULTI },
  { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
];

const getActionByName = ({
  actionName,
  slug,
  onAction,
  item,
  selectedItems,
  onClose,
  fontSize = 'small',
  isPresentationBuilding,
  isDownloadDisabled,
  isDownloading,
  onPresentationDownload,
}: IGetActionByName) => {
  const isHidden = slug === 'trash';

  switch (actionName) {
    case FolderManagerItemActionNames.EDIT:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.EDIT, payload: item });
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.EXPORT:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.EXPORT, payload: item });
          }}
        >
          <ListItemIcon>
            <DownloadOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.RENAME:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.RENAME, payload: item });
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.RESTORE:
      return isHidden ? (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.RESTORE, payload: item });
          }}
        >
          <ListItemIcon>
            <RestoreFromTrashOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Restore</ListItemText>
        </MenuItem>
      ) : null;
    case FolderManagerItemActionNames.SHARE:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerActionTypes.SHARE,
              payload: { row: item, selectedItems: selectedItems },
            });
          }}
        >
          <ListItemIcon>
            <ShareIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.DELETE:
      const label = slug === 'trash' ? 'Delete' : 'Move to trash';
      return (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.DELETE, payload: item });
          }}
        >
          <ListItemIcon>
            <DeleteOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>{label}</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.DOWNLOAD: {
      const isPresentationItem = item.type === FolderManagerItemTypes.PRESENTATION;
      const downloadLabel = isPresentationBuilding ? 'Building...' : isDownloading ? 'Downloading...' : 'Download';
      return (
        <MenuItem
          key={actionName}
          disabled={isDownloadDisabled}
          onClick={(event) => {
            onClose(event);
            if (isPresentationItem && onPresentationDownload) {
              onPresentationDownload();
            } else {
              onAction({ actionType: FolderManagerActionTypes.DOWNLOAD, payload: item });
            }
          }}
        >
          <ListItemIcon>
            <DownloadOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>{downloadLabel}</ListItemText>
        </MenuItem>
      );
    }
    case FolderManagerItemActionNames.DOWNLOAD_DECK:
      return (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.DOWNLOAD_DECK, payload: item });
          }}
        >
          <ListItemIcon>
            <DownloadOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Download Deck</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.UPLOAD:
      return (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.UPLOAD, payload: { pickFiles: true, item: item } });
          }}
        >
          <ListItemIcon>
            <FileUploadIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Upload</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.MOVE:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.MOVE, payload: { item: item } });
          }}
        >
          <ListItemIcon>
            <DriveFileMoveOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.COPY:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.COPY, payload: item });
          }}
        >
          <ListItemIcon>
            <FileCopyOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.MANAGE_VERSIONS:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.MANAGE_VERSIONS, payload: item });
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Manage Versions</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.ADD_TO_PRESENTATION:
      return (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerItemActionNames.ADD_TO_PRESENTATION,
              payload: { selectedItems: selectedItems || [item] },
            });
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Add to Presentation</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION:
      return (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION,
              payload: { selectedItems: selectedItems || [item] },
            });
          }}
        >
          <ListItemIcon>
            <DeleteOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Remove from Presentation</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.MANAGE_THEME:
      return (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerActionTypes.MANAGE_THEME,
              payload: item,
            });
          }}
        >
          <ListItemIcon>
            <BackupTableOutlinedIcon fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Manage Theme</ListItemText>
        </MenuItem>
      );
    case FolderManagerItemActionNames.CLONE:
      return isHidden ? null : (
        <MenuItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerActionTypes.CLONE,
              payload: item,
            });
          }}
        >
          <ListItemIcon>
            <ContentCopyOutlined fontSize={fontSize} />
          </ListItemIcon>
          <ListItemText>Clone</ListItemText>
        </MenuItem>
      );
    default:
      return null;
  }
};

export const FolderManagerItemActions: FC<IFolderManagerItemActions> = ({
  item,
  isLoading,
  selectedItems,
  actions,
  onAction,
  isMulti = false,
}) => {
  const { slug } = useParams();
  const { type: itemType } = item || {};

  const isPresentation = itemType === FolderManagerItemTypes.PRESENTATION;

  // @todo: we need a better way to determine theme: update when api will be ready -->
  const isThemeSlide = slug === 'themes';
  const isDeleted = !!item.trashedAt;

  const mappedActions = useDeepCompareMemo(() => {
    if (!isThemeSlide) {
      return actions;
    }

    return themeActions;
  }, [isThemeSlide, actions]);
  //<-- @todo

  const { data: presentationData } = useGetPresentationQuery(
    { presentationId: item.id },
    { skip: !isPresentation || isDeleted },
  );

  const { isPresentationBuilding, buildData, stopBuildPolling } = usePresentationBuildPolling(
    isPresentation && !isDeleted ? item.id : undefined,
    {
      showNotifications: false,
      hasSlides: (presentationData?.slides?.length ?? 0) > 0,
    },
  );

  const { handleDownload: handlePresentationDownload, isDownloading } = usePresentationDownload({
    presentationId: item.id,
    displayName: item.displayName || item.name,
  });

  const hasNoSlides = presentationData && presentationData.slides?.length === 0;

  const isDownloadDisabled =
    isPresentation &&
    (isPresentationBuilding || isDownloading || !buildData || buildData.status !== 'completed' || hasNoSlides);

  const itemActions = item?.actions ? item.actions : getActionsForItemType(itemType as FolderManagerItemTypes);
  const isDisabled = (item as any)?.actionsDisabled === true;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  // stop polling on unmount
  useEffect(() => {
    return () => {
      stopBuildPolling();
    };
  }, [stopBuildPolling]);

  if (!itemActions?.length || isMulti && !actions?.length) {
    return null;
  }

  return (
    <StyledActionsContainer>
      {isPresentation && isPresentationBuilding && (
        <CircularLoader icon={<SettingsOutlinedIcon />} size={20} centered spinIcon />
      )}
      {isPresentation && isDownloading && <CircularLoader size={20} centered />}

      <IconButton onClick={handleClick} disabled={isDisabled} data-testid="MoreButton">
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <DropdownMenu
        item={item}
        slug={slug}
        actions={mappedActions}
        anchorEl={anchorEl}
        onAction={onAction}
        isLoading={isLoading}
        onClose={handleClose}
        open={Boolean(anchorEl)}
        itemActions={itemActions}
        selectedItems={selectedItems}
        isPresentationBuilding={isPresentationBuilding}
        isDownloadDisabled={isDownloadDisabled}
        isDownloading={isDownloading}
        onPresentationDownload={handlePresentationDownload}
      />
    </StyledActionsContainer>
  );
};
